import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { useAuth } from "@/contexts/AuthContext";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { MapPin, FileDown, X, Map as MapIcon, Clock, ListChecks, Gauge, Fuel } from "lucide-react";
import { Time24Select } from "@/components/Time24Select";
import { splitDateTimeAR, isoFromARDateTime } from "@/lib/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const IN_ICON = L.divIcon({
  className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;background:#16a34a;border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px #16a34a"></div>`,
});
const OUT_ICON = L.divIcon({
  className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;background:#BF3437;border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px #BF3437"></div>`,
});

function fmtDate(iso) { return iso ? new Date(iso).toLocaleString("es-AR") : "—"; }
function fmtDuration(min) {
  if (min == null) return "En curso";
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function CheckinsLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [kmFuel, setKmFuel] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ supervisor_id: searchParams.get("supervisor_id") || "", client_id: "", site_id: "", date_from: "", date_to: "" });
  const [mapRow, setMapRow] = useState(null);
  const [filterSites, setFilterSites] = useState([]);
  // Fix BQ (2026-11-03): el admin cierra una marcación de otro que quedó abierta, con hora y motivo.
  const [closing, setClosing] = useState(null); // { row, date, time, reason }
  const [closeBusy, setCloseBusy] = useState(false);
  const openClose = (r) => { const { date, time } = splitDateTimeAR(r.checkin_at); setClosing({ row: r, date, time, reason: "" }); };
  const submitClose = async () => {
    if (!closing.date || !closing.time) { toast.error("Elegí fecha y hora de egreso"); return; }
    if (closing.reason.trim().length < 3) { toast.error("Indicá el motivo"); return; }
    setCloseBusy(true);
    try {
      await api.post(`/supervisor-checkins/${closing.row.id}/force-close`, { checkout_at: isoFromARDateTime(closing.date, closing.time), reason: closing.reason.trim() });
      toast.success("Marcación cerrada");
      setClosing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCloseBusy(false); }
  };

  const load = async () => {
    try {
      const params = {};
      if (filters.supervisor_id) params.supervisor_id = filters.supervisor_id;
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.site_id) params.site_id = filters.site_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [ck, sm] = await Promise.all([
        api.get("/supervisor-checkins", { params }),
        api.get("/supervisor-checkins/summary", { params }),
      ]);
      setRows(ck.data || []); setSummary(sm.data);
      if (searchParams.get("supervisor_id") && !mapRow) {
        const active = (ck.data || []).find((r) => !r.checkout_at);
        if (active) setMapRow(active);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    // Fix P (2026-09-09): km por GPS necesita un supervisor puntual (admin lo elige
    // en el filtro; el supervisor ya es "él mismo" del lado del backend).
    try {
      const kp = {};
      if (filters.supervisor_id) kp.supervisor_id = filters.supervisor_id;
      if (filters.date_from) kp.date_from = filters.date_from;
      if (filters.date_to) kp.date_to = filters.date_to;
      const { data } = await api.get("/supervisor-routes/km-fuel", { params: kp });
      setKmFuel(data);
    } catch { /* silencioso: no bloquea la lista de marcaciones */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const reqs = [api.get("/clients?include_inactive=true")];
        if (isAdmin) reqs.push(api.get("/users/roster?role=supervisor"));
        const res = await Promise.all(reqs);
        setClients(res[0].data || []);
        if (isAdmin) setSupervisors(res[1].data || []);
      } catch { /* silencioso, no bloquea la lista */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  useEffect(() => {
    if (!filters.client_id) { setFilterSites([]); return; }
    (async () => {
      try { const { data } = await api.get(`/clients/${filters.client_id}/sites`); setFilterSites(data || []); }
      catch { setFilterSites([]); }
    })();
  }, [filters.client_id]);

  const exportUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.supervisor_id) p.set("supervisor_id", filters.supervisor_id);
    if (filters.client_id) p.set("client_id", filters.client_id);
    if (filters.site_id) p.set("site_id", filters.site_id);
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    return `${BACKEND}/api/supervisor-checkins/export.csv?${p.toString()}`;
  }, [filters]);

  const exportPdfUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.supervisor_id) p.set("supervisor_id", filters.supervisor_id);
    if (filters.client_id) p.set("client_id", filters.client_id);
    if (filters.site_id) p.set("site_id", filters.site_id);
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    return `${BACKEND}/api/supervisor-checkins/export.pdf?${p.toString()}`;
  }, [filters]);

  const fmtHours = (min) => `${Math.round((min / 60) * 10) / 10} h`;

  const clear = () => setFilters({ supervisor_id: "", client_id: "", site_id: "", date_from: "", date_to: "" });

  const mapPoints = mapRow ? [
    mapRow.latitude_in != null && mapRow.longitude_in != null ? { pos: [mapRow.latitude_in, mapRow.longitude_in], kind: "in" } : null,
    mapRow.latitude_out != null && mapRow.longitude_out != null ? { pos: [mapRow.latitude_out, mapRow.longitude_out], kind: "out" } : null,
  ].filter(Boolean) : [];
  const mapCenter = mapPoints[0]?.pos || [-32.89, -68.85];

  return (
    <div style={{ padding: "8px 16px" }} data-testid="checkins-log-page">
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Marcaciones de Supervisores</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>
        Ingreso/egreso de cada supervisor por objetivo, con duración y ubicación GPS. {isAdmin ? "Ves las de todo el equipo." : "Ves solo tus propias marcaciones."}
      </p>

      <div className="card" style={{ padding: 14, marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
        {isAdmin && (
          <div className="field" style={{ margin: 0, minWidth: 180 }}>
            <label>Supervisor</label>
            <select value={filters.supervisor_id} onChange={(e) => setFilters({ ...filters, supervisor_id: e.target.value })} data-testid="checkin-filter-supervisor">
              <option value="">Todos</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div className="field" style={{ margin: 0, minWidth: 180 }}>
          <label>Objetivo</label>
          <select value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value, site_id: "" })} data-testid="checkin-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {filterSites.length > 0 && (
          <div className="field" style={{ margin: 0, minWidth: 180 }}>
            <label>Sede</label>
            <select value={filters.site_id} onChange={(e) => setFilters({ ...filters, site_id: e.target.value })} data-testid="checkin-filter-site">
              <option value="">Todas</option>
              {filterSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div className="field" style={{ margin: 0 }}>
          <label>Desde</label>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="checkin-filter-date-from" />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Hasta</label>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="checkin-filter-date-to" />
        </div>
        <button className="btn-ghost" onClick={clear} style={{ padding: "8px 14px", fontSize: 11 }} data-testid="checkin-filter-clear">Limpiar filtros</button>
        <a className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11, marginLeft: "auto" }} href={exportUrl} target="_blank" rel="noreferrer" data-testid="checkins-export">
          <FileDown size={13} style={{ marginRight: 6 }} />Exportar CSV
        </a>
        <a className="btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} href={exportPdfUrl} target="_blank" rel="noreferrer" data-testid="checkins-export-pdf">
          <FileDown size={13} style={{ marginRight: 6 }} />Exportar PDF
        </a>
      </div>

      {/* Fix P (2026-09-09): total de horas/visitas del período + km, en la misma pantalla. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 14 }} data-testid="checkins-summary-cards">
        <SummaryCard icon={Clock} label="Total de horas (período)" value={summary ? fmtHours(summary.grand_total_minutes) : "—"} />
        <SummaryCard icon={ListChecks} label="Total de visitas" value={summary ? summary.grand_total_visits : "—"} />
        <SummaryCard icon={Gauge} label="Km por GPS (checkins)"
          value={kmFuel?.gps ? `${kmFuel.gps.distance_km} km` : "—"}
          sub={!kmFuel?.gps ? "Elegí un supervisor para calcularlo" : kmFuel.gps.reason || `${kmFuel.gps.visits_count} check-in(s) en el período`} />
        <SummaryCard icon={Fuel} label="Km/consumo por odómetro"
          value={kmFuel?.odometer_fuel?.distance_km != null ? `${kmFuel.odometer_fuel.distance_km} km` : "—"}
          sub={kmFuel?.odometer_fuel?.reason || null} />
      </div>
      {summary?.by_supervisor?.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 14 }} data-testid="checkins-summary-by-supervisor">
          {summary.by_supervisor.map((t) => (
            <div key={t.supervisor_id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "10px 16px", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12 }} data-testid={`checkins-summary-row-${t.supervisor_id}`}>
              <b>{t.supervisor_name}</b>
              <span>{fmtHours(t.total_minutes)} · {t.visits_count} visita(s) ({t.client_visits} a clientes, {t.office_visits} a oficinas)</span>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {rows == null && <div style={{ padding: 24, textAlign: "center", color: "var(--invict-text-muted)" }}>Cargando...</div>}
        {rows && rows.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }} data-testid="checkins-empty">Sin marcaciones en este filtro.</div>}
        {rows && rows.map((r) => (
          <div key={r.id} style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`checkin-log-row-${r.id}`}>
            <div style={{ minWidth: 220 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>{r.supervisor_name}{r.supervisor_deleted && <DeletedUserBadge id={r.id} />}</div>
              <div style={{ fontSize: 12, color: "var(--invict-text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
                {r.client_name ? `${r.client_name}${r.site_name ? ` · ${r.site_name}` : ""}` : "Sin objetivo"}
                {r.is_office && <span className="badge blue" style={{ fontSize: 9 }}>Oficina</span>}
                {r.is_backdated && <span className="badge amber" style={{ fontSize: 9 }} data-testid={`checkin-log-backdated-${r.id}`}>Retroactiva</span>}
              </div>
            </div>
            <div style={{ fontSize: 12, minWidth: 150 }}>
              <div>Ingreso: <b>{fmtDate(r.checkin_at)}</b></div>
              <div>Egreso: <b>{r.checkout_at ? fmtDate(r.checkout_at) : "—"}</b></div>
            </div>
            {(r.notes_in || r.notes_out) && (
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic", minWidth: 180, maxWidth: 240 }} data-testid={`checkin-log-notes-${r.id}`}>
                {r.notes_in && <div>Ingreso: "{r.notes_in}"</div>}
                {r.notes_out && <div>Egreso: "{r.notes_out}"</div>}
              </div>
            )}
            <div>
              <span className={`badge ${r.checkout_at ? "gray" : "green"}`} data-testid={`checkin-duration-${r.id}`}>{fmtDuration(r.duration_minutes)}</span>
            </div>
            <button className="btn-ghost" onClick={() => setMapRow(r)} style={{ padding: "7px 12px", fontSize: 11 }} data-testid={`checkin-log-map-${r.id}`}>
              <MapPin size={12} style={{ marginRight: 6 }} />Ver ubicación
            </button>
            {isAdmin && !r.checkout_at && (
              <button className="btn-ghost" onClick={() => openClose(r)} style={{ padding: "7px 12px", fontSize: 11, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`checkin-log-force-close-${r.id}`}>
                <X size={12} style={{ marginRight: 6 }} />Cerrar marcación
              </button>
            )}
            {r.forced_close && <span className="badge amber" style={{ fontSize: 9 }} title={r.forced_close_reason || ""}>Cerrada por {r.forced_close_by_name}</span>}
          </div>
        ))}
      </div>

      {closing && (
        <div className="modal-overlay" onClick={() => setClosing(null)} data-testid="checkin-force-close-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h3 style={{ marginTop: 0 }}>Cerrar marcación abierta</h3>
            <p style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>
              {closing.row.supervisor_name} · {closing.row.client_name || "Sin objetivo"} · ingreso {fmtDate(closing.row.checkin_at)}.
              Elegí la hora de egreso que corresponde: las horas se cuentan hasta ahí.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div className="field"><label>Fecha de egreso</label><input type="date" value={closing.date} onChange={(e) => setClosing({ ...closing, date: e.target.value })} data-testid="checkin-force-close-date"/></div>
              <div className="field"><label>Hora de egreso</label><Time24Select value={closing.time} onChange={(v) => setClosing({ ...closing, time: v })} testId="checkin-force-close-time"/></div>
            </div>
            <div className="field"><label>Motivo *</label><input value={closing.reason} onChange={(e) => setClosing({ ...closing, reason: e.target.value })} placeholder="Ej: usuario eliminado con la visita abierta" data-testid="checkin-force-close-reason"/></div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-ghost" onClick={() => setClosing(null)} style={{ flex: 1 }}>Cancelar</button>
              <button className="btn-primary" onClick={submitClose} disabled={closeBusy} style={{ flex: 1 }} data-testid="checkin-force-close-submit">{closeBusy ? "Cerrando..." : "Cerrar marcación"}</button>
            </div>
          </div>
        </div>
      )}

      {mapRow && (
        <div className="modal-overlay" onClick={() => setMapRow(null)} data-testid="checkin-map-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)" }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{mapRow.supervisor_name}{mapRow.supervisor_deleted && <DeletedUserBadge id={`map-${mapRow.id}`} />} · {mapRow.client_name ? `${mapRow.client_name}${mapRow.site_name ? ` · ${mapRow.site_name}` : ""}` : "Sin objetivo"}</div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{fmtDate(mapRow.checkin_at)}</div>
              </div>
              <button onClick={() => setMapRow(null)} style={{ border: "none", background: "transparent", cursor: "pointer" }} data-testid="checkin-map-close"><X size={18} /></button>
            </div>
            {mapPoints.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 12 }} data-testid="checkin-map-empty">
                <MapIcon size={22} style={{ marginBottom: 8, opacity: 0.5 }} /><br/>Sin datos de ubicación GPS para esta marcación.
              </div>
            ) : (
              <div style={{ height: 320 }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {mapPoints.length === 2 && <Polyline positions={mapPoints.map((p) => p.pos)} color="#BF3437" weight={2} dashArray="6 6" />}
                  {mapPoints.map((p) => (
                    <Marker key={p.kind} position={p.pos} icon={p.kind === "in" ? IN_ICON : OUT_ICON}>
                      <Popup>{p.kind === "in" ? "Ingreso" : "Egreso"}</Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, color: "var(--invict-text-muted)" }}>
        <Icon size={14} /><span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 900, color: "#0A0A0A" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

